/**
 * What the user is looking at, for the assistant (RULES §UI-7, §AI-6). Every page calls
 * `useAssistantContext({...})` with references and summaries — never whole records; the
 * server's page-context resolvers and read tools fetch details.
 */
import { useEffect, useSyncExternalStore } from 'react';

export interface AssistantEntity {
  type: string;
  id: string;
  label?: string;
}

export interface AssistantContextInput {
  /** Human title of the screen, e.g. "Vehicle 1-ABC-123". */
  title: string;
  /** Owning module key (server resolver): "fleet", "home", … */
  module?: string;
  /** Entities the page is about (≤ 20). */
  entities?: AssistantEntity[];
  /** Filters narrowing what is shown (from the URL). */
  filters?: Record<string, unknown>;
  /** Rows the user selected (≤ 50). */
  selection?: AssistantEntity[];
  /** The visible list: type, ids (≤ 200) and the true count. */
  visible?: { type: string; ids: string[]; count?: number };
  /** Anything the page wants to say about itself. */
  note?: string;
  /** Suggested prompts for this page. */
  suggestions?: string[];
}

let current: AssistantContextInput | null = null;
let owner = 0;
let seq = 0;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((fn) => fn());

export function getAssistantContext(): AssistantContextInput | null {
  return current;
}

export function useCurrentAssistantContext(): AssistantContextInput | null {
  return useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    () => current,
    () => current,
  );
}

function capped(ctx: AssistantContextInput): AssistantContextInput {
  return {
    ...ctx,
    entities: ctx.entities?.slice(0, 20),
    selection: ctx.selection?.slice(0, 50),
    visible: ctx.visible ? { ...ctx.visible, ids: ctx.visible.ids.slice(0, 200), count: ctx.visible.count ?? ctx.visible.ids.length } : undefined,
  };
}

/** Register this page's context while it is mounted. The latest mounted page wins. */
export function useAssistantContext(ctx: AssistantContextInput): void {
  // Serialise so a new object with the same content does not re-register.
  const key = JSON.stringify(ctx);
  useEffect(() => {
    const token = ++seq;
    owner = token;
    current = capped(JSON.parse(key) as AssistantContextInput);
    emit();
    return () => {
      if (owner === token) {
        current = null;
        emit();
      }
    };
  }, [key]);
}

/** The `page` object the server expects (nova/ai/tools.py PageContext). */
export function toPagePayload(ctx: AssistantContextInput | null, route: string, projectId: string | undefined): Record<string, unknown> {
  return {
    route,
    module: ctx?.module,
    title: ctx?.title,
    project_id: projectId,
    entities: ctx?.entities ?? [],
    filters: ctx?.filters ?? {},
    selection: ctx?.selection ?? [],
    visible: ctx?.visible,
    note: ctx?.note,
  };
}
